/**
 * ProductSearchResults.jsx
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-253 · Phase 5E-3e · Query Thread Render Extraction · PRODUCT SEARCH RESULTS sub-region
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Predictive Text Rebuild continuation · Refactor Programme 2026 · Phase 5E-3
 * Owner:    E1
 *
 * Purpose:
 *   Extract the PRODUCT SEARCH RESULTS conditional JSX block from the
 *   KnowledgeGraphPage.jsx queryThread render tree. Prerequisite for the
 *   Phase 5E-3f QueryThread coordinator slice · the coordinator target
 *   (about 200 lines) cannot fit under the 500-line ceiling until this
 *   347-line block is extracted first.
 *
 * Guard: rendered only when item.result.type equals 'product_search' AND
 *        item.result.results is present.
 *
 * Sub-regions on screen (byte-verbatim):
 *   - Header row (title · Safety-View toggle · List-vs-Circles view toggle).
 *   - Sources view · list mode (vertical list of product-search hits) OR
 *     circles mode (5-per-row logo circles grid) · both driven by
 *     sourcesViewMode.
 *   - ReactMarkdown comparison summary body (renders when
 *     item.result.comparison_markdown is present).
 *   - Retry button that fires handleProductSearch (renders when
 *     item.result.error is set).
 *
 * Props (13):
 *   item                       · one queryThread entry
 *   idx                        · thread-item index (keys every data-testid)
 *   isMobile                   · mobile-vs-desktop layout branch
 *   browseMode                 · 'safety' | 'direct'
 *   setBrowseMode              · setter
 *   sourcesViewMode            · 'list' | 'circles'
 *   setSourcesViewMode         · setter
 *   productResultsExpanded     · Set<idx> · which thread items are expanded
 *   setProductResultsExpanded  · setter
 *   setSafeBrowserState        · opens Safe Browser overlay on hit click
 *   handleProductSearch        · retry-button click handler
 *   markdownComponents         · shared config passed through from parent
 *   renderContentWithCitations · shared helper passed through from parent
 *
 * Byte-verbatim on every className · style · data-testid · axios URL ·
 * callback body. Zero behaviour change.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeftRight, Globe, LayoutGrid, List, MapPin, Shield, ShoppingCart, Store } from 'lucide-react';
import { renderCitedChildren } from '../../utils/citations';

export function ProductSearchResults({
  item,
  idx,
  isMobile,
  browseMode,
  setBrowseMode,
  sourcesViewMode,
  setSourcesViewMode,
  productResultsExpanded,
  setProductResultsExpanded,
  setSafeBrowserState,
  handleProductSearch,
  markdownComponents,
  renderContentWithCitations,
}) {
  if (item.result.type !== 'product_search' || !item.result.results) return null;
  return (
                  <div className="mb-5" data-testid="product-search-results">
                    {/* D15.1 (Feb 28, 2026, user direction): "Links come
                        first always". The LLM-generated summary prose
                        (which includes the Comparison Summary heading +
                        Best Approach line) used to render here. Now it
                        renders BELOW the bubbles. Order is:
                          1. Results count + bubbles/list (immediate)
                          2. Summary prose
                          3. Visual comparison cards */}

                    {/* SPD v5.86 hotfix (2026-02-14) — the "From the
                        Web" toolbar (Globe icon + "N results" chip +
                        Safety View toggle + List/Grid icons) is now
                        hidden while the search is still in flight AND
                        when zero results came back. Operator called
                        the D17 restore a regression on empty/loading
                        bubbles: showing view-toggle affordances over
                        an empty grey rectangle looks broken. During
                        loading we surface the honest progress message
                        that was already being cycled in
                        handleProductSearch (`item.result.progressMessage`)
                        with a small spinner. On a zero-hit result we
                        say so plainly. */}
                    {(item.isStreaming || !item.result.results || item.result.results.length === 0) ? (
                      <div className="mb-3 flex items-center gap-2.5" data-testid={`product-results-progress-${idx}`}>
                        {item.isStreaming ? (
                          <>
                            <span className="inline-block h-3 w-3 rounded-full border-2 border-cyan-400/60 border-t-transparent animate-spin" />
                            <span className="text-cyan-300/80 text-sm italic">
                              {item.result.progressMessage || 'Searching the web'}&hellip;
                            </span>
                          </>
                        ) : (
                          <>
                            <Globe className="h-4 w-4 text-cyan-600/70" />
                            <span className="text-slate-300/80 text-sm italic">No web results for this query.</span>
                          </>
                        )}
                      </div>
                    ) : (
                    <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'} mb-3`} style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        {/* D17 restore (Feb 28, 2026) — "From the Web" label
                            + result count + Safety View toggle had been
                            limited to KG queries only. Brought back to
                            product_search too since these bubbles ARE web
                            links (retailer pages) and benefit from the
                            same privacy-first browsing affordance. */}
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-cyan-600" />
                          <h4 className={`font-bold text-white ${isMobile ? 'text-sm' : 'text-base'} tracking-wide whitespace-nowrap`}>From the Web</h4>
                          <span className="text-xs text-gray-300 bg-white/10 px-2 py-0.5 rounded-full">{item.result.results.length} results</span>
                        </div>
                        {/* Safety View toggle + hover tooltip — same shape
                            and behaviour as the KG version (line 3520). */}
                        <div className="relative group" data-testid={`browse-mode-toggle-product-${idx}`}>
                          <button
                            onClick={() => setBrowseMode(browseMode === 'safety' ? 'direct' : 'safety')}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              browseMode === 'safety'
                                ? 'bg-emerald-600/90 text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                            data-testid={`browse-mode-safety-product-${idx}`}
                          >
                            <Shield className="h-3.5 w-3.5" />
                            Safety View {browseMode === 'safety' ? 'ON' : 'OFF'}
                          </button>
                          {!isMobile && (
                            <div className="absolute left-0 top-full mt-2 p-4 bg-gray-800 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '380px' }}>
                              <p className="text-lg text-gray-200 leading-relaxed">Safety View allows you to view a website home page without actually visiting it.</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1 flex-shrink-0">
                        <button
                          onClick={() => setSourcesViewMode('list')}
                          className={`p-1.5 rounded transition-colors ${sourcesViewMode === 'list' ? 'bg-white/15 shadow-sm text-cyan-300' : 'text-gray-400 hover:text-white'}`}
                        >
                          <List className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setSourcesViewMode('circles')}
                          className={`p-1.5 rounded transition-colors ${sourcesViewMode === 'circles' ? 'bg-white/15 shadow-sm text-cyan-300' : 'text-gray-400 hover:text-white'}`}
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    )}
                    
                    {/* List View — first 5 visible; rest revealed via toggle (T4 fix) */}
                    {sourcesViewMode === 'list' && (() => {
                      const isExpanded = productResultsExpanded.has(idx);
                      const total = item.result.results.length;
                      const visibleCount = isExpanded ? Math.min(total, 10) : Math.min(total, 5);
                      const hiddenCount = Math.min(total, 10) - 5;
                      return (
                        <>
                          <div className="space-y-2" data-testid={`product-results-list-${idx}`}>
                            {item.result.results.slice(0, visibleCount).map((result, rIdx) => (
                              <div
                                key={rIdx}
                                onClick={(e) => {
                                  e.preventDefault();
                                  // D17 — honour Safety View toggle for
                                  // product_search bubbles too.
                                  if (browseMode === 'safety') {
                                    setSafeBrowserState({ url: result.url, title: result.name });
                                  } else {
                                    window.open(result.url, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all hover:shadow-md cursor-pointer border border-transparent hover:border-cyan-300"
                              >
                                <div 
                                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: '#00d4ff' }}
                                >
                                  {result.source_type === 'local' ? (
                                    <Store className="h-6 w-6 text-white" />
                                  ) : (
                                    <ShoppingCart className="h-6 w-6 text-white" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-semibold text-gray-100 line-clamp-1">{result.name}</p>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      result.source_type === 'local' 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {result.source_type === 'local' ? 'Local' : 'Online'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600 line-clamp-2">{result.description}</p>
                                  {result.price && (
                                    <p className="text-sm font-bold text-cyan-600 mt-1">{result.price}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          {total > 5 && hiddenCount > 0 && (
                            <button
                              type="button"
                              data-testid={`product-results-toggle-${idx}`}
                              onClick={() => {
                                setProductResultsExpanded(prev => {
                                  const next = new Set(prev);
                                  if (next.has(idx)) next.delete(idx); else next.add(idx);
                                  return next;
                                });
                              }}
                              className="mt-3 w-full text-sm py-2 px-4 rounded-lg border border-cyan-500/40 text-cyan-700 hover:bg-cyan-50 transition-colors font-medium"
                            >
                              {isExpanded ? `Show fewer` : `Show ${hiddenCount} more`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Circle View — first 5 visible; rest revealed via toggle (T4 fix) */}
                    {sourcesViewMode === 'circles' && (() => {
                      const isExpanded = productResultsExpanded.has(idx);
                      const total = item.result.results.length;
                      const visibleCount = isExpanded ? Math.min(total, 10) : Math.min(total, 5);
                      const hiddenCount = Math.min(total, 10) - 5;
                      return (
                        <>
                          <div className="grid grid-cols-5 gap-4" data-testid={`product-results-circles-${idx}`}>
                            {item.result.results.slice(0, visibleCount).map((result, rIdx) => (
                              <div
                                key={rIdx}
                                onClick={(e) => {
                                  e.preventDefault();
                                  // D17 — honour Safety View toggle for
                                  // product_search circles too.
                                  if (browseMode === 'safety') {
                                    setSafeBrowserState({ url: result.url, title: result.name });
                                  } else {
                                    window.open(result.url, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                className="flex flex-col items-center gap-2 group cursor-pointer"
                              >
                                <div
                                  className="w-48 h-48 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 ease-out group-hover:scale-150 group-hover:shadow-xl relative overflow-hidden"
                                  style={{ backgroundColor: '#00d4ff' }}
                                >
                                  <div className="absolute inset-0 rounded-full shimmer-glass-subtle" style={{ '--wisp-delay': `${rIdx * 1.2}s` }}></div>
                                  {/* D10.1 fix (Feb 28, 2026): show price +
                                      description content INSIDE the bubble
                                      if available, fall back to the source-
                                      type icon only when neither field has
                                      data. Content is intentionally subtle
                                      at rest (opacity-80, small text); the
                                      group-hover:scale-150 transform on the
                                      parent naturally amplifies it on hover
                                      without needing separate hover styles. */}
                                  {(result.price || result.description) ? (
                                    <div className="relative z-10 flex flex-col items-center justify-center text-white text-center px-4 gap-1 opacity-85 group-hover:opacity-100 transition-opacity">
                                      {result.price && (
                                        <p className="font-bold leading-tight" style={{ fontSize: '0.95rem' }}>
                                          {result.price}
                                        </p>
                                      )}
                                      {result.description && (
                                        <p className="line-clamp-3 leading-tight" style={{ fontSize: '0.65rem' }}>
                                          {result.description}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    result.source_type === 'local' ? (
                                      <Store className="h-16 w-16 text-white relative z-10" />
                                    ) : (
                                      <ShoppingCart className="h-16 w-16 text-white relative z-10" />
                                    )
                                  )}
                                </div>
                                <p className="text-center font-bold line-clamp-2 px-1 group-hover:text-cyan-300 transition-colors" style={{ color: '#f3f4f6', fontSize: '1rem' }}>
                                  {result.name.length > 30 ? result.name.substring(0, 28) + '...' : result.name}
                                </p>
                              </div>
                            ))}
                          </div>
                          {total > 5 && hiddenCount > 0 && (
                            <button
                              type="button"
                              data-testid={`product-results-toggle-circles-${idx}`}
                              onClick={() => {
                                setProductResultsExpanded(prev => {
                                  const next = new Set(prev);
                                  if (next.has(idx)) next.delete(idx); else next.add(idx);
                                  return next;
                                });
                              }}
                              className="mt-4 w-full text-sm py-2 px-4 rounded-lg border border-cyan-500/40 text-cyan-700 hover:bg-cyan-50 transition-colors font-medium"
                            >
                              {isExpanded ? `Show fewer` : `Show ${hiddenCount} more`}
                            </button>
                          )}
                        </>
                      );
                    })()}

                    {/* D15.1 — Summary prose moved here (below bubbles,
                        above the visual comparison cards). Includes the
                        "Comparison Summary" heading + Best Approach line
                        generated by the LLM. */}
                    <div className="mt-5 mb-4" data-testid="product-search-summary">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          ...markdownComponents,
                          // D18 (Feb 28, 2026) — clickable citation badges.
                          // Override paragraph + list-item renderers to scan
                          // for [N] tokens and replace them with a small
                          // cyan pill <button>. Clicking respects the
                          // Safety View toggle (matches bubble behaviour).
                          // The LLM is now instructed to emit [N] not
                          // [Source N], but we tolerate the legacy format
                          // in the regex as a safety net.
                          p: ({ children }) => (
                            <p className="mb-3 text-gray-200 leading-relaxed text-lg">
                              {renderCitedChildren(children, item.result.results || [], browseMode, setSafeBrowserState)}
                            </p>
                          ),
                          li: ({ children }) => (
                            <li className="text-gray-200 leading-relaxed ml-2 text-base">
                              {renderCitedChildren(children, item.result.results || [], browseMode, setSafeBrowserState)}
                            </li>
                          ),
                          ul: ({ children }) => <ul className="list-disc pl-4 mb-4 space-y-1 marker:text-cyan-300">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal pl-4 mb-4 space-y-1 marker:text-cyan-300 marker:font-bold">{children}</ol>,
                          h2: ({ children }) => (
                            <h2 className="text-xl font-bold mb-3 text-white">
                              {renderCitedChildren(children, item.result.results || [], browseMode, setSafeBrowserState)}
                            </h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-lg font-bold mb-2 text-white">
                              {renderCitedChildren(children, item.result.results || [], browseMode, setSafeBrowserState)}
                            </h3>
                          ),
                        }}
                      >
                        {item.result.answer}
                      </ReactMarkdown>
                    </div>

                    {/* Comparison Summary — moved here (D10 reorder, Feb 28,
                        2026) so retailer bubbles render first, analysis
                        second. */}
                    {item.result.comparison && (
                      <div className="mt-5 mb-5 p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                        <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                          <ArrowLeftRight className="h-5 w-5 text-cyan-600" />
                          Local vs Online Comparison
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-white/10 rounded-lg p-3">
                            <h5 className="font-semibold text-green-700 mb-2 flex items-center gap-1">
                              <MapPin className="h-4 w-4" /> Support Local
                            </h5>
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-green-600">Pros:</p>
                              <ul className="text-sm text-gray-200 pl-4 list-disc marker:text-cyan-300">
                                {item.result.comparison.local_pros?.map((pro, i) => (
                                  <li key={i} className="list-disc">{pro}</li>
                                ))}
                              </ul>
                              <p className="text-xs font-medium text-red-600 mt-2">Cons:</p>
                              <ul className="text-sm text-gray-200 pl-4 list-disc marker:text-cyan-300">
                                {item.result.comparison.local_cons?.map((con, i) => (
                                  <li key={i} className="list-disc">{con}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          <div className="bg-white/10 rounded-lg p-3">
                            <h5 className="font-semibold text-blue-700 mb-2 flex items-center gap-1">
                              <Globe className="h-4 w-4" /> Online / Big Players
                            </h5>
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-green-600">Pros:</p>
                              <ul className="text-sm text-gray-200 pl-4 list-disc marker:text-cyan-300">
                                {item.result.comparison.online_pros?.map((pro, i) => (
                                  <li key={i} className="list-disc">{pro}</li>
                                ))}
                              </ul>
                              <p className="text-xs font-medium text-red-600 mt-2">Cons:</p>
                              <ul className="text-sm text-gray-200 pl-4 list-disc marker:text-cyan-300">
                                {item.result.comparison.online_cons?.map((con, i) => (
                                  <li key={i} className="list-disc">{con}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
  );
}
